import { useEffect, useMemo, useRef, useState } from "react";
import { LookupForm } from "./components/LookupForm";
import { ResultsTable } from "./components/ResultsTable";
import { MapPanel } from "./components/MapPanel";
import { TopNav } from "./components/TopNav";
import { WhoisLookup } from "./components/WhoisLookup";
import { DnsHealth } from "./components/DnsHealth";
import { DmarcGenerator } from "./components/DmarcGenerator";
import { SpfGenerator } from "./components/SpfGenerator";
import { DelegationCheck } from "./components/DelegationCheck";
import { CnameChain } from "./components/CnameChain";
import { DnsAlerts } from "./components/DnsAlerts";
import { DnsLookup } from "./components/DnsLookup";
import { WhatIsMyIp } from "./components/WhatIsMyIp";
import { SubnetCalculator } from "./components/SubnetCalculator";
import { Ipv6Tools } from "./components/Ipv6Tools";
import { ReportPage } from "./components/ReportPage";
import { ShareReportButton } from "./components/ShareReportButton";
import { DnsChangeTracker } from "./components/DnsChangeTracker";
import { DnsMigrationChecklist } from "./components/DnsMigrationChecklist";
import { trackFeature } from "./lib/featureAnalytics";
import { ArrowRight, ListChecks, Waypoints } from "lucide-react";
import { streamLookup, ResolverEvent, type DnssecSummary } from "./lib/api";
import { RECORD_TYPES, RecordType } from "./lib/recordTypes";

// Per-page tool selection. Tool landing pages set <meta name="x-tool" content="...">.
// Default (no meta) is the DNS propagation console.
function getTool(): string | null {
  if (typeof document === "undefined") return null;
  const meta = document.querySelector('meta[name="x-tool"]') as HTMLMetaElement | null;
  return meta?.content?.toLowerCase() || null;
}

// Must match worker/resolvers.ts order + location strings.
const RESOLVER_NAMES: [string, string][] = [
  ["Cloudflare",         "Global"],
  ["Google",             "USA"],
  ["Quad9",              "Switzerland"],
  ["OpenDNS",            "USA"],
  ["AdGuard",            "Cyprus"],
  ["NextDNS",            "USA"],
  ["Yandex",             "Russia"],
  ["AliDNS",             "China"],
  ["DNSPod",             "China"],
  ["CleanBrowsing",      "USA"],
  ["Mullvad",            "Sweden"],
  ["ControlD",           "Canada"],
  ["Hurricane Electric", "Global"],
  ["DNS4EU",             "EU"],
];

const DEFAULT_LOOKUP_HOST = "epochcalc.com";

const HOME_PROBLEMS = [
  {
    href: "/dns-not-propagating?type=A",
    label: "DNS is not propagating",
    detail: "See if public resolvers still disagree.",
    tag: "Propagation",
  },
  {
    href: "/domain-still-points-to-old-ip?type=A",
    label: "Old IP is still showing",
    detail: "Find which networks have stale A records.",
    tag: "A record",
  },
  {
    href: "/mx-records-not-updating?type=MX",
    label: "Mail records look wrong",
    detail: "Check MX answers before mail goes missing.",
    tag: "MX",
  },
  {
    href: "/cloudflare-dns-not-updating?type=A",
    label: "Cloudflare changes are delayed",
    detail: "Separate DNS cache from proxy confusion.",
    tag: "Cloudflare",
  },
];

type Row = {
  resolver: string;
  location: string;
  state: "pending" | "ok" | "nxdomain" | "timeout" | "error" | "servfail";
  records?: string[];
  latencyMs?: number;
  message?: string;
  /** AD bit — only `true` positively indicates DNSSEC validation. */
  ad?: boolean;
};

function freshRows(): Row[] {
  return RESOLVER_NAMES.map(([resolver, location]) => ({ resolver, location, state: "pending" }));
}

function getInitial(): { host: string; type: RecordType; updateUrl: boolean } {
  const params = new URLSearchParams(window.location.search);

  // Priority: ?type= query > <meta x-record-type> (set by per-record-type landing pages) > default A
  let candidate = params.get("type");
  if (!candidate) {
    const meta = document.querySelector('meta[name="x-record-type"]') as HTMLMetaElement | null;
    if (meta?.content) candidate = meta.content;
  }
  const t = (candidate ?? "A").toUpperCase();
  const type = (RECORD_TYPES as readonly string[]).includes(t) ? (t as RecordType) : "A";
  const formMode = document.querySelector('meta[name="x-form-mode"]') as HTMLMetaElement | null;
  const defaultHost = formMode?.content ? "" : DEFAULT_LOOKUP_HOST;
  const hostParam = params.get("host");
  const host = hostParam ?? defaultHost;
  return { host, type, updateUrl: hostParam != null };
}

export default function App() {
  const tool = useMemo(getTool, []);
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/report/")) {
    return (
      <>
        <TopNav />
        <ReportPage />
      </>
    );
  }
  if (tool === "whois") {
    return (
      <>
        <TopNav />
        <WhoisLookup />
      </>
    );
  }
  if (tool === "health") {
    return (
      <>
        <TopNav />
        <DnsHealth />
      </>
    );
  }
  if (tool === "dmarc-gen") {
    return (
      <>
        <TopNav />
        <DmarcGenerator />
      </>
    );
  }
  if (tool === "spf-gen") {
    return (
      <>
        <TopNav />
        <SpfGenerator />
      </>
    );
  }
  if (tool === "delegation") {
    return (
      <>
        <TopNav />
        <DelegationCheck />
      </>
    );
  }
  if (tool === "cname-chain") {
    return (
      <>
        <TopNav />
        <CnameChain />
      </>
    );
  }
  if (tool === "alerts") {
    return (
      <>
        <TopNav />
        <DnsAlerts />
      </>
    );
  }
  if (tool === "dns-lookup") {
    return (
      <>
        <TopNav />
        <DnsLookup />
      </>
    );
  }
  if (tool === "my-ip") {
    return (
      <>
        <TopNav />
        <WhatIsMyIp />
      </>
    );
  }
  if (tool === "subnet") {
    return (
      <>
        <TopNav />
        <SubnetCalculator />
      </>
    );
  }
  if (tool === "ipv6") {
    return (
      <>
        <TopNav />
        <Ipv6Tools />
      </>
    );
  }
  if (tool === "change-tracker") {
    return (
      <>
        <TopNav />
        <DnsChangeTracker />
      </>
    );
  }
  if (tool === "migration-checklist") {
    return (
      <>
        <TopNav />
        <DnsMigrationChecklist />
      </>
    );
  }
  return <DnsConsole />;
}

function DnsConsole() {
  const initial = useMemo(getInitial, []);
  const isHomePage = useMemo(() => typeof window !== "undefined" && window.location.pathname === "/", []);
  const [host, setHost] = useState(initial.host);
  const [type, setType] = useState<RecordType>(initial.type);
  const [rows, setRows] = useState<Row[]>(freshRows());
  const [busy, setBusy] = useState(false);
  const [consensus, setConsensus] = useState<Record<string, number> | null>(null);
  const [dnssec, setDnssec] = useState<DnssecSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const runLookup = (h: string, t: RecordType, updateUrl = true) => {
    cancelRef.current?.();
    setHost(h); setType(t);
    setRows(freshRows()); setConsensus(null); setDnssec(null); setError(null); setBusy(true);
    if (updateUrl) {
      const params = new URLSearchParams({ host: h, type: t });
      history.replaceState(null, "", `?${params.toString()}`);
    }
    cancelRef.current = streamLookup(
      h, t,
      (evt: ResolverEvent) => {
        setRows((cur) => cur.map((row) =>
          row.resolver === evt.resolver
            ? { ...row, state: evt.status, records: evt.records, latencyMs: evt.latencyMs, message: evt.message, ad: evt.ad }
            : row));
      },
      (done) => { setConsensus(done.consensus); setDnssec(done.dnssec ?? null); setBusy(false); },
      (msg) => {
        setError(msg);
        // A dropped stream must not leave rows spinning at "querying" under a
        // header that says the check could not finish and a button that says
        // "Check" — three contradictory signals on one screen.
        setRows((cur) => cur.map((row) =>
          row.state === "pending" ? { ...row, state: "error", message: msg } : row));
        setBusy(false);
      },
    );
  };

  useEffect(() => {
    if (initial.host) runLookup(initial.host, initial.type, initial.updateUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = rows.length;
  const topAnswer = consensus
    ? Object.entries(consensus).sort((a, b) => b[1] - a[1])[0]?.[0]
    : undefined;
  const hasResults = busy || consensus != null;
  const answerState = getHomeAnswerState({
    host,
    type,
    busy,
    error,
    consensus,
    topAnswer,
    rows,
    total,
  });
  const resultsPanel = (className = "table-panel") => (
    <section className={className}>
      <div className="panel-title">
        PER-RESOLVER RESULTS
        {hasResults ? (
          <b>
            · {host} / {type}
            {consensus ? ` · ${topConsensusCount(consensus)}/${total} agree` : ""}
          </b>
        ) : null}
      </div>

      {consensus && !busy ? (
        <div className="result-actions" aria-label="Lookup actions">
          <a className="feature-link" href={`/dns-change-tracker?${new URLSearchParams({ host, type })}`}
            onClick={() => trackFeature("change_tracker", "resolver_results")}>
            <Waypoints size={16} aria-hidden="true" /> Track this record
          </a>
          <ShareReportButton key={`${host}:${type}`} host={host} type={type} rows={completedRows(rows)} consensus={consensus} dnssec={dnssec} />
        </div>
      ) : null}

      {hasResults ? (
        <ResultsTable rows={rows} topAnswer={topAnswer} />
      ) : (
        <div className="empty-state">
          <p>Enter a domain in the box above to query 14 public DNS resolvers in parallel.</p>
          <p className="empty-hint">Try <code>epochcalc.com</code>, <code>cloudflare.com</code>, or your own domain.</p>
        </div>
      )}

      {consensus ? (
        <div className="consensus-strip" role="status" aria-live="polite">
          <span className="pct">{topConsensusCount(consensus)} / {total}</span>
          <span className="answer">{topAnswer ?? "—"}</span>
          <span className="meta">
            {consensusMeta(consensus, rows)}
          </span>
        </div>
      ) : null}

      {consensus && dnssec ? <DnssecStrip d={dnssec} /> : null}

      {consensus ? (
        <p className="whois-cross">
          Mid-migration?{" "}
          <a href={`/dns-alerts?host=${encodeURIComponent(host)}&type=${type}`}>
            Get an email when this {type} record changes
          </a>{" "}
          — checked hourly.
        </p>
      ) : null}

    </section>
  );

  if (isHomePage) {
    return (
      <>
        <TopNav />

        <main className="console landing-console">
          <section className={`diagnosis-panel diagnosis-${answerState.tone}`} aria-labelledby="home-heading">
            <div className="landing-intro">
              <span className="landing-eyebrow">DNS propagation checker</span>
              <h2 id="home-heading">Find the resolver that still has the old answer.</h2>
              <p className="landing-lede">
                WhereIsDNS checks your domain from 14 public DNS resolvers at once, then turns the split answers into a clear diagnosis you can act on.
              </p>
            </div>

            <div className="diagnosis-copy">
              <div>
                <span className="answer-eyebrow">{answerState.kicker}</span>
                <h2>{answerState.title}</h2>
                <p>{answerState.body}</p>
              </div>
              <div className="answer-score" aria-label={answerState.scoreLabel}>
                <b>{answerState.score}</b>
                <span>{answerState.scoreCaption}</span>
              </div>
            </div>

            <div className="landing-query" role="search">
              <span className="input-row-label">QUERY</span>
              <LookupForm initialHost={host} initialType={type} onSubmit={runLookup} busy={busy} />
              <span className="input-row-hint" aria-hidden="true">
                <span className="stat-dot" /> 14 resolvers · streaming
              </span>
            </div>

            {host === DEFAULT_LOOKUP_HOST ? (
              <p className="example-note">
                <b>Example lookup:</b> <code>epochcalc.com</code> is prefilled so first-time visitors see real DNS data immediately. Replace it with any domain to run a fresh check.
              </p>
            ) : null}
          </section>

          <div className="migration-prompt">
            <ListChecks size={20} aria-hidden="true" />
            <span>Planning a DNS change?</span>
            <a href={`/dns-migration-checklist?${new URLSearchParams({ host, type })}`}
              onClick={() => trackFeature("migration_checklist", "homepage")}>
              Open migration checklist <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>

          <section className="problem-band" aria-label="Common DNS problems">
            {HOME_PROBLEMS.map((problem, index) => (
              <a className="problem-card" href={problem.href} key={problem.href}>
                <span className="problem-index">{String(index + 1).padStart(2, "0")}</span>
                <span>
                  <b>{problem.label}</b>
                  <small>{problem.detail}</small>
                </span>
                <em>{problem.tag}</em>
              </a>
            ))}
          </section>

          {error ? <div className="error" role="alert">{error}</div> : null}

          <div className="landing-workbench">
            <MapPanel rows={rows} />
            {resultsPanel("table-panel landing-results")}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <TopNav />

      <main className="console">
        <div className="input-row" role="search">
          <span className="input-row-label">QUERY</span>
          <LookupForm initialHost={host} initialType={type} onSubmit={runLookup} busy={busy} />
          <span className="input-row-hint" aria-hidden="true">
            <span className="stat-dot" /> 14 resolvers · streaming
          </span>
        </div>

        {error ? <div className="error" role="alert">{error}</div> : null}

        <div className="body-grid">
          <MapPanel rows={rows} />
          {resultsPanel()}
        </div>
      </main>
    </>
  );
}

type HomeAnswerState = {
  kicker: string;
  title: string;
  body: string;
  score: string;
  scoreCaption: string;
  scoreLabel: string;
  tone: "ready" | "checking" | "ok" | "warn" | "bad";
};

function getHomeAnswerState({
  host,
  type,
  busy,
  error,
  consensus,
  topAnswer,
  rows,
  total,
}: {
  host: string;
  type: RecordType;
  busy: boolean;
  error: string | null;
  consensus: Record<string, number> | null;
  topAnswer?: string;
  rows: Row[];
  total: number;
}): HomeAnswerState {
  if (error) {
    return {
      kicker: host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status",
      title: "This DNS check could not finish.",
      body: "Try the lookup again, or switch record type if you are checking mail, verification, or alias records.",
      score: "!",
      scoreCaption: "needs retry",
      scoreLabel: "Lookup needs a retry",
      tone: "bad",
    };
  }

  if (busy) {
    const answered = rows.filter((row) => row.state !== "pending").length;
    return {
      kicker: host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status",
      title: `Checking ${host || "your domain"} across 14 resolvers.`,
      body: `Answers stream in live so you can see whether the ${type} record is already consistent, still split, or failing somewhere specific.`,
      score: `${answered}/${total}`,
      scoreCaption: "answered",
      scoreLabel: `${answered} of ${total} resolvers have answered`,
      tone: "checking",
    };
  }

  if (consensus) {
    const agreed = topConsensusCount(consensus);
    const failed = rows.filter((row) => row.state === "timeout" || row.state === "error" || row.state === "servfail").length;
    const returned = Object.values(consensus).reduce((sum, count) => sum + count, 0);
    const noUsableAnswer = returned === 0 || (topAnswer ? topAnswer.toUpperCase().includes("SERVFAIL") : false);
    const split = Object.keys(consensus).length > 1 || agreed < total - failed;
    const answer = topAnswer ? formatAnswerPreview(topAnswer) : "No shared answer";

    if (noUsableAnswer) {
      const nxdomain = rows.filter((row) => row.state === "nxdomain").length;
      const kicker = host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status";

      // NXDOMAIN is a definitive answer, not a failure: the name does not
      // exist. Lumping it in with timeouts produced a red panel whose body
      // said "14 of 14 failed" while its score said "0/14 failed".
      if (nxdomain > 0 && failed === 0) {
        return {
          kicker,
          title: `${host} has no ${type} record.`,
          body: `${nxdomain} of ${total} resolvers returned NXDOMAIN — the name does not exist at that type. Check for a typo, or whether the record was ever published.`,
          score: `${nxdomain}/${total}`,
          scoreCaption: "NXDOMAIN",
          scoreLabel: `${nxdomain} of ${total} resolvers returned NXDOMAIN`,
          tone: "warn",
        };
      }

      // Body and score now derive from the same number.
      return {
        kicker,
        title: `${host} is not returning a usable ${type} answer.`,
        body: failed > 0
          ? `${failed} of ${total} resolvers failed or returned no usable answer. Use the resolver table below to see whether this is timeout, SERVFAIL, or missing-record behavior.`
          : `Resolvers answered, but none returned a usable ${type} record. Use the resolver table below to see what each one sent back.`,
        score: `${failed}/${total}`,
        scoreCaption: "failed",
        scoreLabel: `${failed} of ${total} resolvers failed or returned no usable answer`,
        tone: "bad",
      };
    }

    if (split) {
      return {
        kicker: host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status",
        title: `${host} has split DNS answers.`,
        body: `${agreed} of ${total} resolvers agree on ${answer}. The table below shows which networks still see something different.`,
        score: `${agreed}/${total}`,
        scoreCaption: "agree",
        scoreLabel: `${agreed} of ${total} resolvers agree`,
        tone: "warn",
      };
    }

    return {
      kicker: host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status",
      title: `${host} is resolving consistently.`,
      body: `${agreed} of ${total} resolvers returned ${answer} for the ${type} record. Check the map and table below for latency and DNSSEC details.`,
      score: `${agreed}/${total}`,
      scoreCaption: "agree",
      scoreLabel: `${agreed} of ${total} resolvers agree`,
      tone: "ok",
    };
  }

  return {
    kicker: host === DEFAULT_LOOKUP_HOST ? "Live example status" : "Live DNS status",
    title: "Start with the answer, then inspect every resolver.",
    body: "Enter a domain or use the prefilled example to see live DNS data before you click into a deeper troubleshooting guide.",
    score: "14",
    scoreCaption: "resolvers",
    scoreLabel: "Fourteen DNS resolvers available",
    tone: "ready",
  };
}

function formatAnswerPreview(answer: string): string {
  if (answer.length <= 72) return answer;
  return `${answer.slice(0, 69)}...`;
}

/**
 * DNSSEC summary. The wording is deliberately careful:
 *   - "N of M reported" — M is resolvers that actually reported the AD bit,
 *     not all 14, because a resolver that stays silent proves nothing.
 *   - A mixed SERVFAIL/answer split is the classic broken-signature signature:
 *     validating resolvers fail closed while non-validating ones serve on.
 */
function DnssecStrip({ d }: { d: DnssecSummary }) {
  const mixed = d.servfail > 0;
  const allValidated = d.reported > 0 && d.validated === d.reported;
  const tone = mixed ? "bad" : d.validated > 0 ? "ok" : "neutral";
  const label = mixed
    ? `${d.servfail} SERVFAIL`
    : d.reported === 0
      ? "not reported"
      : `${d.validated} / ${d.reported}`;

  let meta: string;
  if (mixed) {
    meta =
      d.validated > 0 || d.reported > 0
        ? "some resolvers refused to answer while others served a result — the classic fingerprint of a broken DNSSEC signature chain (expired RRSIG, or a DS record that no longer matches the DNSKEY). Validating clients are seeing this domain as offline."
        : "resolvers could not produce an answer. On validating resolvers this usually means a broken DNSSEC chain.";
  } else if (d.reported === 0) {
    meta = "no resolver reported the AD bit for this query, so validation status is unknown.";
  } else if (allValidated) {
    meta = "every resolver that reported cryptographically validated this answer against the chain of trust.";
  } else if (d.validated > 0) {
    meta = "validated by some resolvers; the rest either don't validate DNSSEC or the zone isn't signed for them.";
  } else {
    meta = "no resolver asserted validation — the zone is either unsigned or these resolvers don't validate. Signing protects visitors from forged DNS answers.";
  }

  return (
    <div className={`dnssec-strip dnssec-${tone}`} role="status" aria-live="polite">
      <span className="pct">{label}</span>
      <span className="answer">DNSSEC</span>
      <span className="meta">{meta}</span>
    </div>
  );
}

function topConsensusCount(consensus: Record<string, number>): number {
  let max = 0;
  for (const n of Object.values(consensus)) if (n > max) max = n;
  return max;
}

function completedRows(rows: Row[]) {
  return rows.filter((row): row is Row & { state: Exclude<Row["state"], "pending"> } => row.state !== "pending");
}

function consensusMeta(consensus: Record<string, number>, rows: Row[]): string {
  // Same failure set as getHomeAnswerState: SERVFAIL is a non-answer too.
  // Leaving it out made this strip say "all resolvers responded" directly
  // above a DNSSEC strip reporting "N SERVFAIL".
  const failed = rows.filter((r) => r.state === "timeout" || r.state === "error" || r.state === "servfail").map((r) => r.resolver);
  if (failed.length === 0) return "consensus held · all resolvers responded";
  const list = failed.length <= 4 ? failed.join(", ") : `${failed.slice(0, 3).join(", ")} +${failed.length - 3}`;
  const total = Object.values(consensus).reduce((a, b) => a + b, 0);
  return `consensus held · ${failed.length} timeout${failed.length === 1 ? "" : "s"} (${list}) · ${total} returned`;
}
